// components/taxonomy/CategorySubcategorySection.tsx (UPDATED: Firestore-approved taxonomy + seed fallback)
//
// ✅ Loads approved categories/subcategories from Firestore (source of truth)
// ✅ Keeps CATEGORY_SEED as instant fallback + optional merge (so app works even if Firestore empty/offline)
// ✅ Uses lib/taxonomy.ts listApprovedCategories + listApprovedSubcategoriesByCategory
// ✅ Tolerates legacy docs (missing isDeleted/sortOrder handled inside lib functions)
// ✅ Keeps pending add flow (modals handle suggestCategory/suggestSubcategory)

import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import CategoryPickerModal from "@/components/taxonomy/CategoryPickerModal";
import SubcategoryPickerModal from "@/components/taxonomy/SubcategoryPickerModal";

import { auth, db } from "@/lib/firebase";
import {
  listApprovedCategories,
  listApprovedSubcategoriesByCategory,
  normalizeLabel,
  type CategoryDoc,
  type SubcategoryDoc,
} from "@/lib/taxonomy";

import { CATEGORY_SEED } from "@/lib/seed/categories.seed";

export type CategorySubcategoryValue = {
  categoryId: string | null;
  categoryName: string;
  categoryNameNormalized: string;
  categoryStatus: "approved" | "pending";

  subcategoryId: string | null;
  subcategoryName: string;
  subcategoryNameNormalized: string;
  subcategoryStatus: "approved" | "pending";
};

type Props = {
  value: CategorySubcategoryValue;
  onChange: (next: CategorySubcategoryValue) => void;
};

function Field({
  label,
  value,
  hint,
  disabled,
  onPress,
}: {
  label: string;
  value: string;
  hint?: string;
  disabled?: boolean;
  onPress?: () => void;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      style={[styles.field, disabled && styles.fieldDisabled]}
    >
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.valueText}>{value || hint || ""}</Text>
    </Pressable>
  );
}

/**
 * Build "approved" docs from CATEGORY_SEED (no Firestore required for selecting).
 * Seed docs are shaped to match the admin schema (status + isDeleted).
 */
function buildCategoryDocsFromSeed(): CategoryDoc[] {
  return CATEGORY_SEED.map((c) => {
    const name = c.category;
    const normalized = normalizeLabel(name);

    const docOut: CategoryDoc = {
      id: normalized,
      name,
      nameNormalized: normalized,
      status: "approved",
      isDeleted: false,
      sortOrder: 0,
    };

    return docOut;
  });
}

function buildSubcategoryDocsFromSeed(categoryId: string): SubcategoryDoc[] {
  const cat = CATEGORY_SEED.find(
    (c) => normalizeLabel(c.category) === categoryId
  );
  const categoryName = cat?.category ?? "";

  return (cat?.subCategory ?? []).map((sub) => {
    const subId = normalizeLabel(sub);

    const docOut: SubcategoryDoc = {
      id: subId, // ✅ matches Firestore doc id in categories/{categoryId}/subcategories/{subId}
      categoryId,
      categoryName,
      name: sub,
      nameNormalized: subId,
      status: "approved",
      isDeleted: false,
      sortOrder: 0,
    };

    return docOut;
  });
}

function dedupeById<T extends { id: string }>(items: T[]) {
  const map = new Map<string, T>();
  for (const it of items) map.set(it.id, it);
  return Array.from(map.values());
}

export default function CategorySubcategorySection({ value, onChange }: Props) {
  const uid = auth.currentUser?.uid ?? "";

  const [catOpen, setCatOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);

  const [categories, setCategories] = useState<CategoryDoc[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryDoc[]>([]);

  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(false);

  const subDisabled = useMemo(
    () => !value.categoryId,
    [value.categoryId]
  );

  // ✅ Load categories once: seed first (instant) then Firestore (source of truth)
  useEffect(() => {
    let alive = true;

    (async () => {
      // seed first (instant)
      const seedCats = buildCategoryDocsFromSeed();
      if (alive) {
        setCategories(seedCats);
        setLoadingCats(true); // keep spinner briefly while fetching Firestore
      }

      try {
        const fsCats = await listApprovedCategories(db);

        if (!alive) return;

        // Merge seed + Firestore (prefer Firestore by putting it last)
        const merged = dedupeById<CategoryDoc>([...seedCats, ...fsCats]);

        // Optional: sort
        merged.sort(
          (a, b) =>
            (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000) ||
            a.name.localeCompare(b.name)
        );

        setCategories(merged);
      } catch (e: any) {
        console.log("CategorySubcategorySection: load categories error:", e);
        // Keep seed fallback already set
      } finally {
        if (alive) setLoadingCats(false);
      }

      // preload subcategories if already selected and approved
      if (value.categoryId && value.categoryStatus === "approved") {
        setLoadingSubs(true);
        try {
          const seedSubs = buildSubcategoryDocsFromSeed(value.categoryId);
          const fsSubs = await listApprovedSubcategoriesByCategory(
            db,
            value.categoryId
          );

          if (!alive) return;

          const mergedSubs = dedupeById<SubcategoryDoc>([
            ...seedSubs,
            ...fsSubs,
          ]);

          mergedSubs.sort(
            (a, b) =>
              (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000) ||
              a.name.localeCompare(b.name)
          );

          setSubcategories(mergedSubs);
        } catch (e: any) {
          console.log("CategorySubcategorySection: preload subs error:", e);
          if (alive) setSubcategories(buildSubcategoryDocsFromSeed(value.categoryId));
        } finally {
          if (alive) setLoadingSubs(false);
        }
      } else {
        if (alive) setSubcategories([]);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshSubs(categoryId: string) {
    setLoadingSubs(true);
    try {
      const seedSubs = buildSubcategoryDocsFromSeed(categoryId);
      const fsSubs = await listApprovedSubcategoriesByCategory(db, categoryId);

      const merged = dedupeById<SubcategoryDoc>([...seedSubs, ...fsSubs]);

      merged.sort(
        (a, b) =>
          (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000) ||
          a.name.localeCompare(b.name)
      );

      setSubcategories(merged);
    } catch (e: any) {
      console.log("CategorySubcategorySection: refreshSubs error:", e);
      setSubcategories(buildSubcategoryDocsFromSeed(categoryId));
    } finally {
      setLoadingSubs(false);
    }
  }

  const categoryDisplay = value.categoryName
    ? `${value.categoryName}${
        value.categoryStatus === "pending" ? " (Pending)" : ""
      }`
    : "";

  const subcategoryDisplay = value.subcategoryName
    ? `${value.subcategoryName}${
        value.subcategoryStatus === "pending" ? " (Pending)" : ""
      }`
    : "";

  return (
    <View style={{ gap: 12 }}>
      {loadingCats ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={styles.loadingText}>Loading categories…</Text>
        </View>
      ) : null}

      <Field
        label="Category"
        value={categoryDisplay}
        hint="Select category"
        onPress={() => setCatOpen(true)}
      />

      <Field
        label="Subcategory"
        value={subcategoryDisplay}
        hint={
          !value.categoryId
            ? "Select category first"
            : value.categoryStatus === "pending"
            ? "Suggest subcategory under pending category"
            : "Select or suggest subcategory"
        }
        disabled={subDisabled}
        onPress={() => {
          if (subDisabled) return;
          setSubOpen(true);
        }}
      />

      <CategoryPickerModal
        visible={catOpen}
        onClose={() => setCatOpen(false)}
        categories={categories}
        loading={loadingCats}
        createdByUid={uid}
        selectedCategoryId={value.categoryId}
        onSelectApproved={async ({ id, name }) => {
          // close sub modal if it was open (prevents weird state)
          setSubOpen(false);

          const next: CategorySubcategoryValue = {
            ...value,
            categoryId: id,
            categoryName: name,
            categoryNameNormalized: normalizeLabel(name),
            categoryStatus: "approved",

            // clear subcategory on category change
            subcategoryId: null,
            subcategoryName: "",
            subcategoryNameNormalized: "",
            subcategoryStatus: "approved",
          };

          onChange(next);
          await refreshSubs(id);
        }}
        onSelectPending={({ id, name }) => {
          // Pending category keeps its id so a pending subcategory can be suggested under it.
          setSubOpen(false);

          const next: CategorySubcategoryValue = {
            ...value,
            categoryId: id,
            categoryName: name,
            categoryNameNormalized: normalizeLabel(name),
            categoryStatus: "pending",

            subcategoryId: null,
            subcategoryName: "",
            subcategoryNameNormalized: "",
            subcategoryStatus: "approved",
          };

          onChange(next);
          setSubcategories([]);
        }}
      />

      <SubcategoryPickerModal
        visible={subOpen}
        onClose={() => setSubOpen(false)}
        categoryId={value.categoryId}
        categoryName={value.categoryName}
        subcategories={subcategories}
        loading={loadingSubs}
        createdByUid={uid}
        selectedSubcategoryId={value.subcategoryId}
        allowPendingAdd={true}
        onSelectApproved={({ id, name }) => {
          const next: CategorySubcategoryValue = {
            ...value,
            subcategoryId: id,
            subcategoryName: name,
            subcategoryNameNormalized: normalizeLabel(name),
            subcategoryStatus: "approved",
          };
          onChange(next);
        }}
        onSelectPending={({ id, name }) => {
          const next: CategorySubcategoryValue = {
            ...value,
            subcategoryId: id ?? null,
            subcategoryName: name,
            subcategoryNameNormalized: normalizeLabel(name),
            subcategoryStatus: "pending",
          };
          onChange(next);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  field: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    padding: 12,
  },
  fieldDisabled: {
    opacity: 0.5,
  },
  label: {
    fontWeight: "800",
    marginBottom: 6,
  },
  valueText: {
    opacity: 0.85,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
  },
  loadingText: {
    opacity: 0.7,
  },
});
